import '../../../../components/navigation/nav-badge-animations.css';

import type { FC, MouseEventHandler } from 'react';
import { Pressable } from 'react-aria-components';
import { Link } from 'react-router-dom';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { usePulseClass } from '../../../../components/navigation/usePulseClass';

const styles = {
  sm: {
    root: 'size-8',
    icon: 'size-4',
  },
  md: {
    root: 'size-10',
    icon: 'size-5',
  },
  lg: {
    root: 'size-12',
    icon: 'size-6',
  },
};

interface NavItemButtonProps {
  /** Whether the collapsible nav item is open. */
  open?: boolean;
  /** URL to navigate to when the button is clicked. */
  href?: string;
  /** Label text for the button. */
  label: string;
  /** Icon component to display. */
  icon: FC<{ className?: string }>;
  /** Whether the button is currently active. */
  current?: boolean;
  /** Size of the button. */
  size?: 'sm' | 'md' | 'lg';
  /** Handler for click events. */
  onClick?: MouseEventHandler;
  /** Additional CSS classes to apply to the button. */
  className?: string;
  /** Placement of the tooltip. */
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  /** Badge text to display in the top-right corner of the button (collapsed mode). */
  badge?: string | number;
  /** Number that increments when the badge should pulse. */
  pulseKey?: number;
  /** Suffix appended to aria-label for screen readers, e.g. "3 unread". */
  ariaLabelSuffix?: string;
  /** data-component handle for the badge span (e2e). */
  badgeDataComponent?: string;
}

export const NavItemButton = ({
  current: current,
  label,
  href,
  icon: Icon,
  size = 'md',
  className,
  tooltipPlacement = 'right',
  onClick,
  badge,
  pulseKey,
  ariaLabelSuffix,
  badgeDataComponent,
}: NavItemButtonProps) => {
  const isPulsing = usePulseClass(pulseKey, 1000);
  const ariaLabel = ariaLabelSuffix ? `${label}, ${ariaLabelSuffix}` : label;

  const commonClassName = cx(
    'relative flex w-full cursor-pointer items-center justify-center rounded-md bg-primary p-2 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
    current && 'bg-active text-fg-quaternary_hover hover:bg-secondary_hover',
    styles[size].root,
    className
  );

  const badgeElement = badge !== undefined && (
    <span
      aria-hidden='true'
      data-component={badgeDataComponent}
      className={cx(
        'absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-md bg-primary px-1 text-[10px] font-medium leading-none text-secondary shadow-xs ring-1 ring-secondary ring-inset',
        isPulsing && 'nav-badge-pulse'
      )}
    >
      {badge}
    </span>
  );

  const content = (
    <>
      <Icon
        aria-hidden='true'
        className={cx('shrink-0 transition-inherit-all', styles[size].icon)}
      />
      {badgeElement}
    </>
  );

  // Render as button when no href is provided
  if (!href) {
    return (
      <Tooltip title={label} placement={tooltipPlacement}>
        <Pressable>
          <button
            type='button'
            aria-label={ariaLabel}
            onClick={onClick}
            className={commonClassName}
          >
            {content}
          </button>
        </Pressable>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={label} placement={tooltipPlacement}>
      <Pressable>
        <Link
          to={href}
          aria-label={ariaLabel}
          onClick={onClick}
          className={commonClassName}
        >
          {content}
        </Link>
      </Pressable>
    </Tooltip>
  );
};
